import { z } from "zod";

const envSchema = z.object({
  // Defaults make the app boot with zero env files: dev uses the Vite proxy
  // (same-origin /api → backend), so no VITE_API_BASE_URL is required locally.
  VITE_ENV: z.enum(["production", "staging", "development", "local"]).default("local"),
  VITE_API_BASE_URL: z
    .string()
    .transform((v) => (v === "" ? undefined : v))
    .pipe(z.string().url().optional())
    .optional(),
  VITE_STOREFRONT_URL: z.string().url().optional(),
  // Empty string means "Sentry off" (e.g. local dev where DSN is blank in .env).
  VITE_SENTRY_DSN: z
    .string()
    .transform((v) => (v === "" ? undefined : v))
    .pipe(z.string().url().optional())
    .optional(),
  VITE_SENTRY_RELEASE: z.string().optional(),
  VITE_DEFAULT_TENANT_CODE: z.string().default("nutrayan"),
  VITE_FEATURE_DEBUG_PANEL: z
    .union([z.boolean(), z.string()])
    .transform((v) => v === true || v === "true")
    .default(false),
  VITE_FEATURE_AI_ASSISTANT: z
    .union([z.boolean(), z.string()])
    .transform((v) => v === true || v === "true")
    .default(false),
  VITE_FEATURE_DARK_MODE: z
    .union([z.boolean(), z.string()])
    .transform((v) => v === true || v === "true")
    .default(false),
});

export type Env = z.infer<typeof envSchema>;

function readRawEnv() {
  const raw = import.meta.env;
  return {
    VITE_ENV: raw.VITE_ENV,
    VITE_API_BASE_URL: raw.VITE_API_BASE_URL,
    VITE_STOREFRONT_URL: raw.VITE_STOREFRONT_URL,
    VITE_SENTRY_DSN: raw.VITE_SENTRY_DSN,
    VITE_SENTRY_RELEASE: raw.VITE_SENTRY_RELEASE,
    VITE_DEFAULT_TENANT_CODE: raw.VITE_DEFAULT_TENANT_CODE,
    VITE_FEATURE_DEBUG_PANEL: raw.VITE_FEATURE_DEBUG_PANEL,
    VITE_FEATURE_AI_ASSISTANT: raw.VITE_FEATURE_AI_ASSISTANT,
    VITE_FEATURE_DARK_MODE: raw.VITE_FEATURE_DARK_MODE,
  };
}

let cached: Env | undefined;

export function getEnv(): Env {
  if (cached) return cached;
  const result = envSchema.safeParse(readRawEnv());
  if (!result.success) {
    // eslint-disable-next-line no-console
    console.error("Invalid environment variables:", result.error.flatten().fieldErrors);
    throw new Error("Environment validation failed — see logs.");
  }
  cached = result.data;
  return cached;
}
